import React, { useState, useRef, useEffect, useCallback } from "react";
import {
  Box,
  Typography,
  Button,
  IconButton,
  Tooltip,
  Link,
  Alert,
} from "@mui/material";
import {
  PictureAsPdf,
  Forum as ForumIcon,
  NoteAlt as NoteAltIcon,
  AddRounded,
  HelpRounded,
} from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import { useChartMindContext } from "context/ChartMindSession";
import { useAuth } from "hooks/useAuth";
import ConfirmDialog from "components/common/ConfirmDialog";
import ChartMindHelpDialog from "components/dashboard/chartmind/ChartMindHelpDialog";
import { invokeLLMPrompt } from "services/llmService";
import { retrieveClinicalReferences } from "services/clinicalReferencesService";
import CustomMessageInput from "components/chat_new/CustomMessageInput";
import { ChatMessage, TypingIndicator } from "components/chat";
import PdfViewerDialog from "components/common/PdfViewerDialog";
import {
  mergeSourcesByUrl,
  displayLabel,
  stripSourceCitations,
} from "utils/clinicalSources";
import { buildPatientContextPayload } from "utils/patientContext";
import { DASHBOARD_CONTENT_HEIGHT } from "constants/layout";
import { TopBarActions, TopBarTitle } from "components/dashboard/layout/TopBarSlots";

const PROMPT_ID = "chartmind-peerview-consultation";

// ─── source / citation helpers (same pattern as AskMeAnythingPage) ──────────

function SourceLinks({ sources, onOpenPdf }) {
  if (!sources?.length) return null;
  return (
    <Box sx={{ mt: 1, pt: 1, borderTop: 1, borderColor: "divider" }}>
      <Typography
        variant="caption"
        color="text.secondary"
        sx={{ display: "block", mb: 0.5 }}
      >
        Source documents
      </Typography>
      {sources.map((s) => (
        <Box
          key={s.downloadURL}
          sx={{ display: "flex", alignItems: "center", gap: 0.5, mb: 0.25 }}
        >
          <PictureAsPdf
            sx={{ fontSize: "0.9rem", color: "error.main", flexShrink: 0 }}
          />
          <Link
            component="button"
            variant="caption"
            onClick={() => onOpenPdf(s)}
            sx={{ textAlign: "left", wordBreak: "break-word" }}
          >
            {displayLabel(s)}
          </Link>
        </Box>
      ))}
    </Box>
  );
}

// ─── context builder ─────────────────────────────────────────────────────────

function buildEncounterContext(session) {
  const parts = [];

  // The patient's own record is deliberately NOT built into this message. It travels as the
  // patientContext payload on the LLM call instead, where callLLMAPI puts it in the system
  // prompt behind the authoritative wrapper and the precedence rule. Mixing it back in here
  // would put chart data on the same footing as the transcript.

  const transcript =
    session.recording?.debouncedTranscript || session.recording?.transcript;
  if (transcript?.trim()) {
    parts.push(`ENCOUNTER TRANSCRIPT:\n${transcript.trim()}`);
  }

  const dxName = (d) =>
    d.condition || d.diagnosis || d.name || (typeof d === "string" ? d : "");

  const ddx = session.diagnosis?.ddxData;
  if (ddx) {
    const primary = (ddx.primary_diagnoses || [])
      .map(dxName)
      .filter(Boolean)
      .join(", ");
    const alt = (ddx.alternative_diagnoses || [])
      .map(dxName)
      .filter(Boolean)
      .join(", ");
    if (primary) parts.push(`AI DIFFERENTIAL (PRIMARY): ${primary}`);
    if (alt) parts.push(`AI DIFFERENTIAL (ALTERNATIVE): ${alt}`);
  }

  const selected = session.diagnosis?.selectedDiagnoses;
  if (selected?.length) {
    parts.push(
      `SELECTED DIAGNOSES: ${selected.map(dxName).filter(Boolean).join(", ")}`,
    );
  }

  const confirmed = session.confirmDiagnosis?.selectedConfirmedDiagnoses;
  if (confirmed?.length) {
    parts.push(
      `CONFIRMED DIAGNOSES: ${confirmed.map(dxName).filter(Boolean).join(", ")}`,
    );
  }

  const treatmentPlan = session.treatment?.planData;
  if (treatmentPlan) {
    const str =
      typeof treatmentPlan === "string"
        ? treatmentPlan
        : JSON.stringify(treatmentPlan, null, 2);
    parts.push(`TREATMENT PLAN:\n${str}`);
  }

  const noteSections = session.chart?.noteSections;
  if (noteSections && Object.keys(noteSections).length > 0) {
    const chartStr = Object.entries(noteSections)
      .map(([k, v]) => `${k.toUpperCase()}:\n${v}`)
      .join("\n\n");
    parts.push(`CLINICAL NOTE:\n${chartStr}`);
  }

  return parts.join("\n\n");
}

function hasEncounterData(session) {
  return !!(
    session.recording?.transcript ||
    session.diagnosis?.ddxData ||
    session.diagnosis?.selectedDiagnoses?.length ||
    (session.chart?.noteSections &&
      Object.keys(session.chart.noteSections).length > 0)
  );
}

// ─── no-encounter state ───────────────────────────────────────────────────────

function NoEncounterBody() {
  const navigate = useNavigate();
  return (
    <Box
      sx={{
        flex: 1,
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        px: 3,
        textAlign: "center",
      }}
    >
      <Box sx={{ mb: 3 }}>
        <ForumIcon sx={{ fontSize: 64 }} color="primary" />
      </Box>
      <Typography
        variant="h4"
        sx={{ mb: 2, fontSize: { xs: "1.75rem", sm: "2.125rem" } }}
      >
        PeerView Case Consultation
      </Typography>
      <Typography
        variant="body1"
        sx={{ mb: 4, color: "text.secondary", lineHeight: 1.6, maxWidth: 480 }}
      >
        Ask questions about your ChartMind encounter. Start recording an encounter to get started.
      </Typography>
      <Button
        variant="contained"
        onClick={() => navigate("/dashboard/chartmind")}
      >
        Start a ChartMind Encounter
      </Button>
    </Box>
  );
}

// ─── main component ───────────────────────────────────────────────────────────

const PeerViewPage = () => {
  const navigate = useNavigate();
  const session = useChartMindContext();
  const { user, isGlobalAdmin, userData, organizationId } = useAuth();
  const isOrgAdmin = userData?.role === "admin" && Boolean(organizationId);

  const messages = session.peerViewMessages;
  const setMessages = session.setPeerViewMessages;

  const [inputValue, setInputValue] = useState("");
  const [isTyping, setIsTyping] = useState(false);
  const [error, setError] = useState(null);
  const [confirmNewOpen, setConfirmNewOpen] = useState(false);
  const [helpOpen, setHelpOpen] = useState(false);
  const [pdfViewer, setPdfViewer] = useState({
    open: false,
    url: null,
    title: null,
  });

  const chatBoxRef = useRef(null);

  useEffect(() => {
    if (chatBoxRef.current) {
      chatBoxRef.current.scrollTop = chatBoxRef.current.scrollHeight;
    }
  }, [messages, isTyping]);

  const handleOpenPdf = (source) => {
    if (source?.downloadURL) {
      setPdfViewer({
        open: true,
        url: source.downloadURL,
        title: displayLabel(source),
      });
    }
  };

  const performNewEncounter = useCallback(() => {
    session.session.clearSession();
    session.clearPeerViewMessages();
    setError(null);
  }, [session]);

  const handleNewEncounterClick = useCallback(() => {
    if (hasEncounterData(session)) {
      setConfirmNewOpen(true);
      return;
    }
    performNewEncounter();
  }, [session, performNewEncounter]);

  const handleConfirmNewEncounter = useCallback(() => {
    performNewEncounter();
    setConfirmNewOpen(false);
  }, [performNewEncounter]);

  const handleSend = async (text) => {
    const trimmed = (typeof text === "string" ? text : inputValue).trim();
    if (!trimmed || isTyping) return;

    setInputValue("");
    setError(null);
    setMessages((prev) => [
      ...prev,
      { role: "user", content: trimmed, sources: [] },
    ]);
    setIsTyping(true);

    // Re-read context on every send so it reflects latest session state
    const contextBlock = buildEncounterContext(session);
    const fullMessage = contextBlock
      ? `[ChartMind Encounter Context]\n${contextBlock}\n\n[QUESTION]\n${trimmed}`
      : trimmed;

    try {
      const [result, clinicalRefs] = await Promise.all([
        invokeLLMPrompt({
          promptId: PROMPT_ID,
          userMessage: fullMessage,
          patientContext: buildPatientContextPayload(session.patientContext?.value),
          includeEmbeddedDocuments: true,
          embeddedDocumentsOptions: { chunkLimit: 8 },
        }),
        retrieveClinicalReferences(trimmed, { promptId: PROMPT_ID }).catch(
          () => [],
        ),
      ]);

      const embeddedDocs = result.embeddedDocuments?.documents ?? [];
      const sources = mergeSourcesByUrl(embeddedDocs, clinicalRefs);
      const content = stripSourceCitations(result.output || "(No response)");

      setMessages((prev) => [...prev, { role: "assistant", content, sources }]);
    } catch (err) {
      const msg = err.message || "Request failed.";
      setError(msg);
      setMessages((prev) => [
        ...prev,
        {
          role: "assistant",
          content: `Error: ${msg}`,
          isError: true,
          sources: [],
        },
      ]);
    } finally {
      setIsTyping(false);
    }
  };

  const noEncounter = !hasEncounterData(session);

  return (
    <Box
      sx={{
        height: DASHBOARD_CONTENT_HEIGHT,
        width: "100%",
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
      }}
    >
      {/* The page's corner of the dashboard top bar (TopBarContext). */}
      <TopBarTitle>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, pl: { sm: 1 } }}>
          <ForumIcon sx={{ fontSize: 20, color: "text.secondary" }} />
          <Typography variant="subtitle2" color="text.secondary">
            PeerView
          </Typography>
        </Box>
      </TopBarTitle>
      <TopBarActions>
        <Tooltip title="Help">
          <IconButton size="small" onClick={() => setHelpOpen(true)} sx={{ color: "text.secondary" }}>
            <HelpRounded fontSize="small" />
          </IconButton>
        </Tooltip>
        <Tooltip title="Back to ChartMind">
          <IconButton
            onClick={() => navigate("/dashboard/chartmind")}
            size="small"
            sx={{ color: "text.secondary" }}
          >
            <NoteAltIcon fontSize="small" />
          </IconButton>
        </Tooltip>
        <Tooltip title="New Encounter">
          <IconButton onClick={handleNewEncounterClick} size="small" sx={{ color: "text.secondary" }}>
            <AddRounded fontSize="small" />
          </IconButton>
        </Tooltip>
      </TopBarActions>

      {/* No-encounter splash */}
      {noEncounter && <NoEncounterBody />}

      {/* Chat area + PDF dialog — only when there's an active encounter */}
      {!noEncounter && <><Box
        sx={{
          margin: "0 auto",
          flex: 1,
          display: "flex",
          flexDirection: "column",
          minHeight: 0,
          width: "100%",
          maxWidth: "970px",
          px: 2,
          ...(messages.length === 0
            ? {
                justifyContent: "flex-start",
                alignItems: "center",
                paddingTop: { sm: "1rem", md: "20vh" },
              }
            : { justifyContent: "flex-start" }),
        }}
      >
        {/* Intro / empty state */}
        {messages.length === 0 && (
          <Box
            sx={{
              textAlign: "center",
              py: 0,
              px: 2,
              maxWidth: "600px",
              width: "100%",
            }}
          >
            <Box sx={{ mb: 3, display: "flex", justifyContent: "center" }}>
              <ForumIcon sx={{ fontSize: 64 }} color="primary" />
            </Box>
            <Typography
              variant="h4"
              sx={{
                mb: 2,
                fontSize: { xs: "1.75rem", sm: "2.125rem" },
              }}
            >
              PeerView Case Consultation
            </Typography>
            <Typography
              variant="body1"
              sx={{ mb: 3, color: "text.secondary", lineHeight: 1.6 }}
            >
              Ask a question about your ChartMind encounter.</Typography>
          </Box>
        )}

        {/* Error alert */}
        {error && (
          <Alert
            severity="error"
            onClose={() => setError(null)}
            sx={{ mb: 2, width: "100%", maxWidth: "600px" }}
          >
            {error}
          </Alert>
        )}

        {/* Messages */}
        {messages.length > 0 && (
          <Box
            ref={chatBoxRef}
            sx={{
              flex: 1,
              overflowY: "auto",
              minHeight: 0,
              width: "100%",
              display: "flex",
              flexDirection: "column",
              gap: 4,
              py: 2,
              mb: 1,
            }}
          >
            {messages.map((msg, i) => (
              <ChatMessage
                key={i}
                role={msg.role === "user" ? "user" : "assistant"}
                text={msg.content}
                error={Boolean(msg.isError)}
                copyable
                footer={
                  msg.role === "assistant" && msg.sources?.length > 0 ? (
                    <SourceLinks sources={msg.sources} onOpenPdf={handleOpenPdf} />
                  ) : null
                }
              />
            ))}

            {isTyping && <TypingIndicator />}
          </Box>
        )}

        {/* Input */}
        <Box
          sx={{
            flexShrink: 0,
            width: "100%",
            ...(messages.length === 0
              ? { position: "relative", maxWidth: "600px" }
              : { marginBottom: "6px" }),
          }}
        >
          <CustomMessageInput
            inputValue={inputValue}
            setInputValue={setInputValue}
            handleSendRequest={handleSend}
            userData={null}
            hasMessages={messages.length > 0}
          />
        </Box>
      </Box>

      {/* PDF Viewer */}
      <PdfViewerDialog
        open={pdfViewer.open}
        url={pdfViewer.url}
        title={pdfViewer.title}
        onClose={() => setPdfViewer({ open: false, url: null, title: null })}
      /></>}

      {/* Simulation warning for admins */}
      {(isGlobalAdmin || isOrgAdmin) && (
        <Alert
          severity="warning"
          variant="standard"
          action={
            <Button
              color="inherit"
              size="small"
              onClick={() => navigate("/dashboard/prompts")}
            >
              Manage Prompts
            </Button>
          }
          sx={{
            flexShrink: 0,
            borderRadius: 0,
            fontSize: "0.75rem",
            py: 0.25,
            alignItems: "center",
            "& .MuiAlert-icon": { fontSize: "1rem" },
          }}
        >
          Simulation mode for prompt development. Not intended for medical use.
        </Alert>
      )}

      <ChartMindHelpDialog open={helpOpen} onClose={() => setHelpOpen(false)} />

      <ConfirmDialog
        open={confirmNewOpen}
        onClose={() => setConfirmNewOpen(false)}
        title="Start a new encounter?"
        message="Starting a new encounter will clear your current ChartMind session and this PeerView conversation."
        confirmLabel="Start new"
        confirmColor="primary"
        onConfirm={handleConfirmNewEncounter}
      />
    </Box>
  );
};

export default PeerViewPage;
